import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaInfoCircle, FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { apiFetch } from '../utils/api';
import './AdminPage.css';

const INITIAL_TENANT_FORM_STATE = {
  name: '', business_number: '', address: '', city: '', role: 'l3',
  province: 'ON', postal_code: '', phone: '', contact_person: '',
  mobile: '', email: '', industry: 'General',
};

function AdminPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState({ roles: true, tenants: true });

  const [tenantFormData, setTenantFormData] = useState(INITIAL_TENANT_FORM_STATE);
  const [editingTenant, setEditingTenant] = useState(null);
  const [showTenantForm, setShowTenantForm] = useState(false);


  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Admin routes don't require tenantId, so we pass null
      const tenantsData = await apiFetch('/admin/tenants', null);
      setTenants(Array.isArray(tenantsData) ? tenantsData : []);
    } catch (error) {
      console.error("Failed to fetch tenants", error);
      setTenants([]);
    } finally {
      setLoading(prev => ({ ...prev, tenants: false }));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (showTenantForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [showTenantForm]);

  const handleTenantInputChange = (e) => {
    const { name, value } = e.target;
    setTenantFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetTenantForm = () => {
    setTenantFormData(INITIAL_TENANT_FORM_STATE);
    setEditingTenant(null);
    setShowTenantForm(false);
  };

  const handleEditTenant = async (tenant) => {
    // Fetch associated users for the tenant being edited
    try {
      const users = await apiFetch(`/admin/users-by-tenant/${tenant.tenant_identifier}`, null);
      const associated_users = Array.isArray(users) ? users.join(', ') : '';
      setTenantFormData({
        ...INITIAL_TENANT_FORM_STATE, // Start with defaults
        ...tenant, // Overlay with existing data
        associated_users,
        enabled_date: tenant.enabled_date ? tenant.enabled_date.split('T')[0] : '',
        expiry_date: tenant.expiry_date ? tenant.expiry_date.split('T')[0] : '',
      });
    } catch (error) {
      console.error("Error fetching users for tenant", error);
      // Fallback to empty users string
      setTenantFormData({ ...INITIAL_TENANT_FORM_STATE, ...tenant, associated_users: '' });
    }
    setEditingTenant(tenant);
    setShowTenantForm(true);
  };

  const handleTenantSubmit = async (e) => {
    e.preventDefault();
    const endpoint = editingTenant ? `/admin/tenants/${editingTenant.id}` : '/admin/tenants';
    const method = editingTenant ? 'PUT' : 'POST';

    try {
      await apiFetch(endpoint, null, {
        method,
        body: { ...tenantFormData, is_active: tenantFormData.is_active ?? 1 }
      });
      resetTenantForm();
      fetchData();
    } catch (error) {
      console.error("Tenant submit error:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleDeleteTenant = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this tenant? This is a soft delete and can be undone.')) return;
    try {
      await apiFetch(`/admin/tenants/${id}`, null, { method: 'DELETE' });
      fetchData();
    } catch (error) {
      console.error("Tenant delete error:", error);
    }
  };

  const handleViewTenantDashboard = (tenant) => {
    // Construct the organization object expected by App.js and OrganizationDashboard
    const selectedOrg = {
      id: 1, // The primary organization in a tenant's DB always has id = 1
      name: tenant.name,
      tenantId: tenant.tenant_identifier,
    };
    localStorage.setItem('selectedOrg', JSON.stringify(selectedOrg));
    navigate('/org-management'); // Navigate to the organization management route for the selected tenant
  };
  return (
    <div className="ap-admin container-fluid py-3 py-md-4 px-3 px-md-4">

      {/* Page Header */}
      <div className="mb-4">
        <h1 className="ap-page-title h4 mb-1">System Administration</h1>
        <p className="ap-page-subtitle mb-0">Manage user access, roles, and tenant organizations.</p>
      </div>

      {/* Tenant Management */}
      <div className="mt-4 mt-md-5">
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-3 gap-2">
          <h2 className="ap-section-title h5 mb-0">Tenant Management</h2>
              <div style={{display: 'flex', gap: 8}}>
                <button className="ap-btn ap-btn-secondary" onClick={() => navigate('/admin/db-management')}>
                  DB Management
                </button>
                <button className="ap-btn ap-btn-primary" onClick={() => { resetTenantForm(); setShowTenantForm(true); }}>
            <FaPlus className="me-2" /> Create New Tenant
                </button>
              </div>
        </div>
        <div className="ap-card">
          {loading.tenants ? (
            <div className="ap-loading">Loading tenants…</div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="table-responsive d-none d-md-block">
                <table className="ap-table mb-0">
                  <thead>
                    <tr>
                      <th>Tenant Name</th>
                      <th>Associated Users</th>
                      <th>Role</th>
                      <th>Enabled</th>
                      <th>Expires</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <div className="ap-empty-state">No tenants have been created yet.</div>
                        </td>
                      </tr>
                    )}
                    {tenants.map(tenant => (
                      <tr key={tenant.id}>
                        <td className="fw-semibold">
                          <div className="d-flex align-items-center gap-2">
                            {tenant.name}
                            <span className="ap-info-icon">
                              <FaInfoCircle />
                              <span className="ap-tooltip">Identifier: {tenant.tenant_identifier}</span>
                            </span>
                          </div>
                        </td>
                        <td className="small" style={{ whiteSpace: 'normal', maxWidth: '300px' }}>
                          {tenant.associated_users?.join(', ')}
                        </td>
                        <td>
                          <span className={`ap-pill ${tenant.role === 'admin' ? 'ap-pill-admin' : 'ap-pill-role'}`}>
                            {(tenant.role || 'N/A').toUpperCase()}
                          </span>
                        </td>
                        <td className="small">{tenant.enabled_date ? new Date(tenant.enabled_date).toLocaleDateString() : 'N/A'}</td>
                        <td className="small">{tenant.expiry_date ? new Date(tenant.expiry_date).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <span className={`ap-pill ${tenant.is_active ? 'ap-pill-active' : 'ap-pill-inactive'}`}>
                            {tenant.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button className="ap-icon-btn" onClick={() => handleViewTenantDashboard(tenant)} aria-label="View tenant dashboard">
                              <FaEye />
                            </button>
                            <button className="ap-icon-btn" onClick={() => handleEditTenant(tenant)} aria-label="Edit tenant">
                              <FaEdit />
                            </button>
                            {tenant.is_active ? (
                              <button className="ap-icon-btn ap-icon-danger" onClick={() => handleDeleteTenant(tenant.id)} aria-label="Deactivate tenant">
                                <FaTrash />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none ap-mobile-card-list">
                {tenants.length === 0 && <div className="ap-empty-state">No tenants have been created yet.</div>}
                {tenants.map(tenant => (
                  <div key={tenant.id} className="ap-mobile-card">
                    <div className="ap-mobile-card-header">
                      <div className="fw-bold text-primary">{tenant.name}</div>
                      <span className={`ap-pill ${tenant.is_active ? 'ap-pill-active' : 'ap-pill-inactive'}`}>{tenant.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div className="ap-mobile-card-body">
                      <div className="ap-mobile-card-row"><strong>Role:</strong> <span className={`ap-pill ${tenant.role === 'admin' ? 'ap-pill-admin' : 'ap-pill-role'}`}>{(tenant.role || 'N/A').toUpperCase()}</span></div>
                      <div className="ap-mobile-card-row"><strong>Users:</strong> <span className="text-muted">{tenant.associated_users?.join(', ') || 'N/A'}</span></div>
                      <div className="ap-mobile-card-row"><strong>Subscription:</strong> <span className="text-muted">{tenant.enabled_date ? new Date(tenant.enabled_date).toLocaleDateString() : 'N/A'} to {tenant.expiry_date ? new Date(tenant.expiry_date).toLocaleDateString() : 'N/A'}</span></div>
                    </div>
                    <div className="ap-mobile-card-footer">
                      <button className="ap-btn ap-btn-secondary w-100 mb-2" onClick={() => handleViewTenantDashboard(tenant)}>
                        <FaEye className="me-2" /> View Tenant
                      </button>
                      <button className="ap-btn ap-btn-secondary w-100" onClick={() => handleEditTenant(tenant)}>Manage Tenant</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create / Edit Tenant Modal */}
      {showTenantForm && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-end" 
          style={{ zIndex: 1040 }}
          role="dialog"
          aria-modal="true"
        >
          {/* Backdrop */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50" 
            style={{ transition: 'opacity 0.3s' }} 
            onClick={resetTenantForm}
            aria-hidden="true"
          ></div>

          {/* Sliding Panel */}
          <div 
            className="position-relative h-100 bg-white shadow-lg d-flex flex-column" 
            style={{ width: '100%', maxWidth: '600px', zIndex: 1050, animation: 'slideInRight 0.3s ease-out forwards' }}
          >
            <form onSubmit={handleTenantSubmit} className="ap-drawer-form">
              <div className="ap-modal-header">
                <h5 className="ap-modal-title" id="ap-tenant-modal-title">
                  {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close shadow-none bg-light rounded-circle p-2" 
                  onClick={resetTenantForm}
                  aria-label="Close"
                ></button>
              </div>
              <div className="ap-modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="ap-form-label" htmlFor="ap-tenant-name">Tenant Name</label>
                    <input id="ap-tenant-name" type="text" name="name" className="ap-form-control" value={tenantFormData.name} onChange={handleTenantInputChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="ap-form-label" htmlFor="ap-tenant-bn">Business Number</label>
                    <input id="ap-tenant-bn" type="text" name="business_number" className="ap-form-control" value={tenantFormData.business_number} onChange={handleTenantInputChange} required />
                  </div>
                  <div className="col-12">
                    <label className="ap-form-label" htmlFor="ap-tenant-address">Address</label><input id="ap-tenant-address" type="text" name="address" className="ap-form-control" value={tenantFormData.address} onChange={handleTenantInputChange} required />
                  </div>
                  <div className="col-md-5">
                    <label className="ap-form-label" htmlFor="ap-tenant-city">City</label>
                    <input id="ap-tenant-city" type="text" name="city" className="ap-form-control" value={tenantFormData.city} onChange={handleTenantInputChange} required />
                  </div>
                  <div className="col-md-3">
                    <label className="ap-form-label" htmlFor="ap-tenant-province">Province</label>
                    <select id="ap-tenant-province" name="province" className="ap-form-select" value={tenantFormData.province} onChange={handleTenantInputChange}>
                      <option value="ON">ON</option><option value="BC">BC</option><option value="AB">AB</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="ap-form-label" htmlFor="ap-tenant-postal">Postal Code</label>
                    <input id="ap-tenant-postal" type="text" name="postal_code" className="ap-form-control" value={tenantFormData.postal_code} onChange={handleTenantInputChange} required />
                  </div>
                  <div className="col-md-6">
                    <label className="ap-form-label" htmlFor="ap-tenant-contact">Contact Person</label>
                    <input id="ap-tenant-contact" type="text" name="contact_person" className="ap-form-control" value={tenantFormData.contact_person} onChange={handleTenantInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="ap-form-label" htmlFor="ap-tenant-email">Contact Email</label>
                    <input id="ap-tenant-email" type="email" name="email" className="ap-form-control" value={tenantFormData.email} onChange={handleTenantInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="ap-form-label" htmlFor="ap-tenant-phone">Phone</label>
                    <input id="ap-tenant-phone" type="tel" name="phone" className="ap-form-control" value={tenantFormData.phone} onChange={handleTenantInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="ap-form-label" htmlFor="ap-tenant-mobile">Mobile</label>
                    <input id="ap-tenant-mobile" type="tel" name="mobile" className="ap-form-control" value={tenantFormData.mobile} onChange={handleTenantInputChange} />
                  </div>
                  <div className="col-md-4">
                    <label className="ap-form-label" htmlFor="ap-tenant-status">Status</label>
                    <select
                      id="ap-tenant-status"
                      name="is_active"
                      className="ap-form-select"
                      value={tenantFormData.is_active ?? 1}
                      onChange={e => setTenantFormData(prev => ({ ...prev, is_active: parseInt(e.target.value, 10) }))}
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="ap-form-label" htmlFor="ap-tenant-role">Role</label>
                    <select id="ap-tenant-role" name="role" className="ap-form-select" value={tenantFormData.role} onChange={handleTenantInputChange}>
                      <option value="l1">L1 (Single Org, 1 Employee)</option>
                      <option value="l2">L2 (Single Org, 10 Employees)</option>
                      <option value="l3">L3 (Multi-Org Access)</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="ap-form-label">Enabled Date</label>
                    <input type="date" name="enabled_date" className="ap-form-control" value={tenantFormData.enabled_date} onChange={handleTenantInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="ap-form-label">Expiry Date</label>
                    <input type="date" name="expiry_date" className="ap-form-control" value={tenantFormData.expiry_date} onChange={handleTenantInputChange} />
                  </div>
                  <div className="col-12">
                    <label className="ap-form-label" htmlFor="ap-tenant-users">Associated User Emails</label>
                    <textarea id="ap-tenant-users" name="associated_users" className="ap-form-control" value={tenantFormData.associated_users} onChange={handleTenantInputChange} rows="1" placeholder="user1@example.com, user2@example.com..."></textarea>
                  </div>
                </div>
              </div>
              <div className="ap-modal-footer">
                <button type="button" className="ap-btn ap-btn-secondary" onClick={resetTenantForm}>Cancel</button>
                <button type="submit" className="ap-btn ap-btn-primary">{editingTenant ? 'Save Changes' : 'Create Tenant'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;